import { useState, useRef, useEffect } from 'react';

import { Form, Select as CSelect, Tabs } from 'antd';
import {
  Popover,
  PopoverPlacement,
  SelectOption as Option,
  Space,
  Text,
} from 'farmhub-ui-core';

import { GoogleApi } from 'api';
import { useAppDispatch, useAppSelector } from 'app';
import {
  findProvincesAndDistricts,
  selectLocation,
  setDistrictSelect,
  setProvinceSelect,
} from 'slice';
import { theme } from 'styled';

import {
  Wrap,
  WrapChoiceLocation,
  WrapError,
  WrapSelect,
  WrapTab,
} from './styles';

interface LocationSelectProps {
  form: any;
  provinceCodeInit: string;
  districtCodeInit: string;
  wardCodeInit: string;
  showTitle?: boolean;
  disabled?: boolean;
  required?: boolean;
  isVerticalForm?: boolean;
  marginBottom?: number;
  isLoadingAddress: boolean;
  isNotShowLabel?: boolean;
  hasErrorCode?: boolean;
  idPopup: string;
  heightSpace?: number;
  containerClassName?: string;
  height?: string;
  onFocus?: (e?: any) => void;
  setHasErrorCode?: (e: boolean) => void;
}

const LocationSelectV1 = ({
  form,
  provinceCodeInit,
  districtCodeInit,
  wardCodeInit,
  showTitle = true,
  disabled = false,
  isVerticalForm = false,
  required = false,
  isLoadingAddress = false,
  marginBottom = 24,
  isNotShowLabel,
  hasErrorCode,
  idPopup,
  heightSpace = 24,
  containerClassName,
  height = theme.select.height.md,
  onFocus,
  setHasErrorCode,
}: LocationSelectProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const provincesData = useAppSelector(
    state => state.location.provinceData
  ).filter(item => item.id !== 'all');

  const districtsData = useAppSelector(state => state.location.districtData);
  const isLoaded = useAppSelector(state => state.location.isLoaded);
  const wardsData = useAppSelector(state => state.location.wardData);
  const [provinceCodeSelected, setProvinceCodeSelected] = useState<string>();
  const [districtCodeSelected, setDistrictCodeSelected] = useState<string>();
  const [wardCodeSelected, setWardCodeSelected] = useState<string>();
  const [tabLocation, setTabLocation] = useState<string>('1');
  const [isVisible, setIsVisible] = useState<boolean>();
  const [searchValue, setSearchValue] = useState<string>();
  const [listAddress, setListAddress] = useState<any[]>([]);
  const [finishInput, setFinishInput] = useState<boolean>();

  const innerRef = useRef();

  const [address, setAddress] = useState<string>();
  const findLocation = useAppSelector(state => state.location.findLocation);
  const location = useAppSelector(selectLocation);

  useEffect(() => {
    if (!provinceCodeSelected && !districtCodeSelected && !wardCodeSelected) {
      setSearchValue(undefined);
      setAddress(undefined);
    }
  }, [districtCodeSelected, provinceCodeSelected, wardCodeSelected]);

  useEffect(() => {
    const initLocationData = async () => {
      try {
        await dispatch(findProvincesAndDistricts()).unwrap();
      } catch (error) {
        console.log('An error occurred while finding locations', error);
      }
    };
    if (!location?.isLoaded && location.data?.length == 0) {
      initLocationData();
    }
  }, [dispatch, location.data?.length, location?.isLoaded]);

  useEffect(() => {
    let result = '';

    if (provinceCodeSelected) {
      const provinceResult = provincesData.find(
        item => Number(item.code) == Number(provinceCodeSelected)
      );

      if (provinceResult) {
        result += provinceResult.fullName;

        const districtResult = districtsData?.find(
          item => Number(item.code) == Number(districtCodeSelected)
        );
        if (districtResult) {
          result = `${result}, ${districtResult.fullName}`;

          const wardResult = wardsData?.find(
            item => Number(item.code) == Number(wardCodeSelected)
          );
          if (wardResult) {
            result = `${result}, ${wardResult.fullName}`;

            if (finishInput) {
              setIsVisible(false);
              setFinishInput(false);
            }
          }
        }

        setAddress(result);
        form.setFieldsValue({ allCode: result });
        setSearchValue('');
      }
    }
  }, [
    tabLocation,
    provinceCodeSelected,
    districtCodeSelected,
    wardCodeSelected,
  ]);

  useEffect(() => {
    setListAddress([]);
    setProvinceCodeSelected('');
    setDistrictCodeSelected('');
    setWardCodeSelected('');

    if (isLoaded) {
      if (provinceCodeInit) {
        dispatch(setProvinceSelect(provinceCodeInit));
        setProvinceCodeSelected(provinceCodeInit);
        setTabLocation('2');
      } else {
        setTabLocation('1');
      }

      if (provinceCodeInit && districtCodeInit) {
        setTabLocation('3');

        dispatch(
          setDistrictSelect({
            districtCode: districtCodeInit,
            provinceCode: provinceCodeInit,
          })
        );

        form.setFieldsValue({
          districtCode: districtCodeInit,
          provinceCode: provinceCodeInit,
          wardCode: wardCodeInit,
        });

        setDistrictCodeSelected(districtCodeInit);
        setWardCodeSelected(wardCodeInit);
      }
    }
  }, [
    isLoaded,
    provinceCodeInit,
    districtCodeInit,
    wardCodeInit,
    isLoadingAddress,
  ]);

  useEffect(() => {
    if (findLocation) {
      form.setFieldsValue({
        address: findLocation.address,
        provinceCode: findLocation.provinceCode,
        districtCode: findLocation.districtCode,
        wardCode: findLocation.wardCode,
        latitude: findLocation.latitude,
        longitude: findLocation.longitude,
      });

      setProvinceCodeSelected(findLocation.provinceCode);
      setDistrictCodeSelected(findLocation.districtCode);
      setWardCodeSelected(findLocation.wardCode);
    }
  }, [isLoadingAddress]);

  const handleSelectProvince = (value: string) => {
    let result = value;
    if (result.length == 1) {
      result = `0${value}`;
    }

    setProvinceCodeSelected(result);

    form.setFieldsValue({
      provinceCode: result,
      districtCode: null,
      wardCode: null,
    });

    setProvinceCodeSelected(result);
    setDistrictCodeSelected('');
    setTabLocation('2');
    dispatch(setProvinceSelect(result));
  };

  const handleSelectDistrict = (value: string) => {
    if (provinceCodeSelected && value) {
      let result = value;

      if (result.length == 2) {
        result = `0${value}`;
      } else if (result.length == 1) {
        result = `00${value}`;
      }

      form.setFieldsValue({
        provinceCode: provinceCodeSelected,
        districtCode: result,
        wardCode: null,
      });

      dispatch(
        setDistrictSelect({
          districtCode: result,
          provinceCode: provinceCodeSelected,
        })
      );

      setTabLocation('3');
      setDistrictCodeSelected(result);
      setWardCodeSelected('');
    }
  };

  const handleSelectWard = (value: string) => {
    if (provinceCodeSelected && districtCodeSelected) {
      let result = value;
      if (result?.length == 4) {
        result = `0${value}`;
      } else if (result?.length == 3) {
        result = `00${value}`;
      } else if (result?.length == 2) {
        result = `000${value}`;
      } else if (result?.length == 1) {
        result = `0000${value}`;
      }

      form.setFieldsValue({
        provinceCode: provinceCodeSelected,
        districtCode: districtCodeSelected,
        wardCode: result,
      });

      setTabLocation('1');
      setWardCodeSelected(result);
      setFinishInput(true);
      setIsVisible(false);
    }
  };

  const onSearch = async (value: string) => {
    if (value.length > 0) {
      const { data } = await GoogleApi.getDetailAddressesByText({
        searchText: value,
      });
      setListAddress(data || []);
      setIsVisible(false);
    } else {
      setIsVisible(true);
      setAddress(undefined);
      setSearchValue(undefined);
      setListAddress([]);
    }
  };

  const onChangeSearch = async (value: string) => {
    if (value.length > 0) {
      setTabLocation('1');
      const { data } = await GoogleApi.getDetailAddressesByPlaceID({
        placeID: value,
      });
      setProvinceCodeSelected('');
      setDistrictCodeSelected('');
      setWardCodeSelected('');
      setSearchValue('');
      setAddress('');

      form.setFieldsValue({
        provinceCode: data.provinceCode,
        districtCode: data.districtCode,
        wardCode: data.wardCode,
      });

      if (data.provinceCode) {
        setTabLocation('2');
        setProvinceCodeSelected(data.provinceCode);
        dispatch(setProvinceSelect(data.provinceCode));

        form.setFieldsValue({
          provinceCode: data.provinceCode,
        });

        if (data.districtCode) {
          setTabLocation('3');
          setDistrictCodeSelected(data.districtCode);
          setDistrictSelect(data.districtCode);

          form.setFieldsValue({
            districtCode: data.districtCode,
            wardCode: data.wardCode,
          });

          dispatch(
            setDistrictSelect({
              districtCode: data.districtCode,
              provinceCode: data.provinceCode,
            })
          );

          if (data.wardCode) {
            setWardCodeSelected(data.wardCode);
            form.setFieldsValue({
              wardCode: data.wardCode,
            });

            setIsVisible(false);
            setTabLocation('1');
          } else {
            setIsVisible(true);
          }
        } else {
          form.setFieldsValue({
            districtCode: null,
            wardCode: null,
          });

          setIsVisible(true);
        }
      } else {
        form.setFieldsValue({
          provinceCode: null,
        });

        setIsVisible(true);
      }

      if (
        data?.provinceCode &&
        data?.districtCode &&
        data?.wardCode &&
        data?.latitude &&
        data?.longitude
      ) {
        setIsVisible(false);
        setTabLocation('1');
      }
    } else {
      setIsVisible(true);
      setTabLocation('1');
    }
  };

  useEffect(() => {
    document.addEventListener('click', e => {
      const ref: any = innerRef.current;

      if (!ref?.contains(e.target)) {
        setIsVisible(false);
      }
    });
  });

  return (
    <Wrap>
      {!isNotShowLabel && (
        <Text style={{ paddingBottom: '8px' }} weight="bold">
          <Text color="red">*</Text> Tỉnh thành/Quận huyện/Xã phường:
        </Text>
      )}
      <Popover
        containerId={idPopup}
        content={
          <WrapChoiceLocation>
            <Tabs
              activeKey={tabLocation}
              onChange={e => setTabLocation(e)}
              onClick={e => {
                setIsVisible(true);
              }}
            >
              <Tabs.TabPane key="1" tab="Tỉnh thành">
                <WrapTab>
                  <Form.Item name="provinceCode">
                    <ul className="ul">
                      {provincesData?.map((province: any) => (
                        // eslint-disable-next-line react/button-has-type
                        <button
                          key={province.code}
                          className={
                            province.code == provinceCodeSelected
                              ? 'active'
                              : ''
                          }
                          type="button"
                          value={province.code}
                          onClick={e =>
                            handleSelectProvince(
                              String((e.target as HTMLInputElement).value)
                            )
                          }
                        >
                          {isVerticalForm ? province.fullName : province.name}
                        </button>
                      ))}
                    </ul>
                  </Form.Item>
                </WrapTab>
              </Tabs.TabPane>
              <Tabs.TabPane
                key="2"
                disabled={districtsData?.length == 0}
                tab="Quận huyện"
              >
                <WrapTab>
                  <Form.Item name="districtCode">
                    <ul>
                      {districtsData?.map((district: any) => (
                        // eslint-disable-next-line react/button-has-type
                        <button
                          key={district.code}
                          className={
                            district.code == districtCodeSelected
                              ? 'active'
                              : ''
                          }
                          type="button"
                          value={district.code}
                          onClick={e =>
                            handleSelectDistrict(
                              String((e.target as HTMLInputElement).value)
                            )
                          }
                        >
                          {district.fullName}
                        </button>
                      ))}
                    </ul>
                  </Form.Item>
                </WrapTab>
              </Tabs.TabPane>
              <Tabs.TabPane
                key="3"
                disabled={wardsData?.length == 0}
                tab="Xã phường"
              >
                <WrapTab>
                  <Form.Item name="wardCode">
                    <ul>
                      {wardsData?.map((ward: any) => (
                        // eslint-disable-next-line react/button-has-type
                        <button
                          key={ward.code}
                          className={
                            ward.code == wardCodeSelected ? 'active' : ''
                          }
                          type="button"
                          value={ward.code}
                          onClick={e =>
                            handleSelectWard(
                              String((e.target as HTMLInputElement).value)
                            )
                          }
                        >
                          {ward.fullName}
                        </button>
                      ))}
                    </ul>
                  </Form.Item>
                </WrapTab>
              </Tabs.TabPane>
            </Tabs>
          </WrapChoiceLocation>
        }
        placement={PopoverPlacement.BottomLeft}
        styles={{ width: '100%', position: 'absolute' }}
        trigger="click"
        visible={isVisible}
        onVisibleChange={() => isVisible}
      >
        <div
          ref={innerRef as any}
          id={idPopup}
          style={{ position: 'relative' }}
        >
          <Space height={8} />
          <WrapSelect selectOpen className={containerClassName} height={height}>
            <Form.Item
              name="allCode"
              rules={[
                {
                  required: true,
                  message: 'Bạn chưa nhập thông tin tỉnh, huyện, xã!',
                },
              ]}
            >
              <CSelect
                showSearch
                className="select"
                notFoundContent=""
                optionFilterProp="children"
                placeholder="Tỉnh thành/Quận huyện/Xã phường"
                style={{
                  width: '100%',
                }}
                value={address || searchValue}
                onChange={onChangeSearch}
                onFocus={e => {
                  if (
                    searchValue?.length == 0 ||
                    address?.length == 0 ||
                    !address ||
                    !searchValue
                  ) {
                    setIsVisible(true);
                    setListAddress([]);
                  }
                  onFocus && onFocus();
                  setHasErrorCode && setHasErrorCode(false);
                }}
                onSearch={onSearch}
              >
                {listAddress?.length > 0 &&
                  listAddress.map(item => (
                    <Option value={item.placeID}>{item.address}</Option>
                  ))}
              </CSelect>
            </Form.Item>
            {hasErrorCode ? (
              <WrapError>Bạn chưa nhập thông tin tỉnh, huyện, xã!</WrapError>
            ) : null}
          </WrapSelect>
        </div>
      </Popover>
    </Wrap>
  );
};

export default LocationSelectV1;
